/**
 * Lightweight i18n - zero dependency, sub-millisecond, type-safe.
 *
 * Picks language from (in order):
 *   1. localStorage "shpaid.lang" (explicit user choice)
 *   2. default: "id" (Bahasa Indonesia)
 *
 * Usage:
 *   import { t } from "@/lib/i18n";
 *   t("welcome.title");
 */

import { en } from "./en";
import { id } from "./id";

export const LOCALES = { en, id } as const;
export type Locale = keyof typeof LOCALES;
export type TranslationKey = keyof typeof en;

const STORAGE_KEY = "shpaid.lang";

function detectLang(): Locale {
  // Honour an explicit saved choice; otherwise default to Indonesian on first load.
  try {
    const saved = localStorage.getItem(STORAGE_KEY) as Locale | null;
    if (saved && saved in LOCALES) return saved;
  } catch { /* noop */ }

  return "id";
}

let currentLang: Locale = detectLang();

export function getLocale(): Locale { return currentLang; }

export function setLocale(lang: Locale): void {
  if (!(lang in LOCALES)) return;
  currentLang = lang;
  try { localStorage.setItem(STORAGE_KEY, lang); } catch { /* noop */ }
  document.documentElement.lang = lang;
  // Cheap re-render trigger: dispatch a window event app code can listen to
  window.dispatchEvent(new Event("locale-change"));
}

export function t(key: TranslationKey, vars?: Record<string, string | number>): string {
  const dict = LOCALES[currentLang] ?? en;
  let str = (dict as Record<string, string>)[key] ?? (en as Record<string, string>)[key] ?? key;
  if (vars) {
    for (const [k, v] of Object.entries(vars)) {
      str = str.replace(new RegExp(`\\{${k}\\}`, "g"), String(v));
    }
  }
  return str;
}

// Set initial <html lang> on module import
if (typeof document !== "undefined") {
  document.documentElement.lang = currentLang;
}
